'use client';

import React, { createContext, useContext, useEffect } from 'react';
import isEqual from 'lodash/isEqual';
import useRouter from '@framework/hooks/useNextRouter';
import { getInitialState, IInitialState } from '@/creditflowresearch/application/Trace/TraceFilters/helpers';
import { useTraceFiltersContext } from '@/creditflowresearch/application/Trace/TraceFilters/TraceFiltersContext';
import { useTraceAssetClassesContext } from '@/creditflowresearch/application/Trace/TraceAssetClassesContext';
import { TTraceFiltersPayload } from '@/creditflowresearch/domain/vo/Trace/IMarketTradesPayload';
import { removeQueryParams } from '@framework/router';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { useFiltersContext } from '@framework/ui/Organisms/Filters/FiltersContext';
import { useCheckboxerContext } from '@framework/ui/Organisms/Filters/Checkboxer/CheckboxerContext';
import { useMounted } from '@/creditflowresearch/ui/common/hooks';

const useTrace = (initial: { filter?: TTraceFiltersPayload }) => {
  const { getFiltersPayload, initFilters, payloadChange } = useTraceFiltersContext();
  const { query: queryParams, push } = useRouter();
  const { currentAssetClass } = useTraceAssetClassesContext();
  const { isPro } = useUserPlanContext();
  const { filtersUpdate } = useFiltersContext();
  const { checkboxes } = useCheckboxerContext();
  const isMounted = useMounted();

  const initTrace = async () => {
    let isHashFiltersPayload = false;
    let filtersState = getInitialState() as IInitialState;
    let initialFiltersPayload = getFiltersPayload(filtersState, checkboxes);
    let filtersPayload = initialFiltersPayload;

    if (currentAssetClass.id) {
      initialFiltersPayload.includedAssetClassId = currentAssetClass.id;
    }

    if ('hash' in queryParams) {
      const { filter: hashPayload } = initial;
      if (hashPayload) {
        isHashFiltersPayload = true;
        filtersPayload = hashPayload;
      }

      if (isEqual(hashPayload, initialFiltersPayload)) { // for cases when hash is incorrect
        push({ query: removeQueryParams(queryParams, ['hash']) });
      }
    }

    if (Object.keys(filtersPayload).length) {
      payloadChange(filtersPayload);
      if (isHashFiltersPayload) {
        await initFilters(filtersPayload, undefined, isHashFiltersPayload, null, filtersUpdate);
      } else {
        await initFilters(filtersPayload, filtersState, false, null, filtersUpdate);
      }
    }
  };

  useEffect(() => {
    if (!isMounted || !currentAssetClass || !isPro) {
      return;
    }
    initTrace();
  }, [currentAssetClass, isPro, isMounted]);

  return {};
};

const TraceContext = createContext(null);

export const useTraceContext = () => {
  const context = useContext(TraceContext);
  if (!context) {
    throw new Error('useTraceContext must be used within the TraceContext');
  }
  return context;
};

interface IProps {
  initial: { filter?: TTraceFiltersPayload },
  children: React.ReactNode,
}

const TraceContextProvider = ({ initial, children }: IProps) => (
  <TraceContext.Provider value={ useTrace(initial) }>
    { children }
  </TraceContext.Provider>
);

export default TraceContextProvider;
